
import { useQuery } from "@tanstack/react-query";
import { supabase } from '@/lib/supabaseClient';
import { useToast } from "./use-toast";
import { useAccount } from "@/contexts/AccountContext";

export const useAnimalsBySpecies = () => {
  const { toast } = useToast();
  const { currentAccount } = useAccount();

  return useQuery({
    queryKey: ["animalsBySpecies", currentAccount?.account_id],
    queryFn: async () => {
      try {
        if (!currentAccount) {
          return [];
        }

        const { data: animals, error: animalsError } = await supabase
          .from("animals")
          .select('species')
          .eq('account_id', currentAccount.account_id);

        if (animalsError) {
          console.error('Error fetching animals:', animalsError);
          toast({
            title: "Error fetching animals",
            description: animalsError.message,
            variant: "destructive",
          });
          return [];
        }

        if (!animals?.length) {
          return [];
        }

        // Create a map of species counts
        const counts: Record<string, number> = {};
        animals.forEach((animal: { species: string; }) => {
          if (animal.species) {
            const species = animal.species.trim();
            counts[species] = (counts[species] || 0) + 1;
          }
        });

        // Convert to array of species and counts
        return Object.entries(counts).map(([species, count]) => ({
          species: species.charAt(0).toUpperCase() + species.slice(1),
          count,
        }));
      } catch (error) {
        console.error('Unexpected error:', error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
        return [];
      }
    },
    retry: 1,
    enabled: !!currentAccount,
  });
};
